import { useRef, useState } from 'react';
import type { ReportGranularity } from '@six-pockets/shared';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { useDismiss } from '@/hooks/useDismiss';
import { usePopoverSide } from '@/hooks/usePopoverSide';
import { MAX_SPAN, currentKey, fromMonthKey, selectionLabel, shiftKey, toMonthKey } from '@/features/reports/utils';
import { cx } from '@/utils/cx';
import { fromDateKey, toDateKey } from '@/utils/date';
import { Button } from './Button';
import styles from '@/styles/components/PeriodPicker/style.scss';

interface PeriodPickerProps {
  granularity: ReportGranularity;
  /** YYYY-MM-DD (days) or YYYY-MM (months); from = to for a single one. */
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  /** Accessible name, e.g. "Report period". */
  label: string;
  /** False for one day (month) only: no One / Range switch, and only the single presets. Default true. */
  ranges?: boolean;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface Preset {
  label: string;
  from: string;
  to: string;
}

/** The footer shortcuts; with `ranges` false, only the single day (month) ones. */
function presets(g: ReportGranularity, ranges: boolean): Preset[] {
  return allPresets(g).filter((p) => ranges || p.from === p.to);
}

function allPresets(g: ReportGranularity): Preset[] {
  const now = currentKey(g);
  if (g === 'day') {
    return [
      { label: 'Today', from: now, to: now },
      { label: 'Last 7 days', from: shiftKey('day', now, -6), to: now },
      { label: 'This month', from: `${now.slice(0, 7)}-01`, to: now },
    ];
  }
  return [
    { label: 'This month', from: now, to: now },
    { label: 'Last 6 months', from: shiftKey('month', now, -5), to: now },
    { label: 'This year', from: `${now.slice(0, 4)}-01`, to: now },
  ];
}

/**
 * A field-styled button showing the selection ("Today", "1 Oct → 5 Oct", "This month") that opens a calendar of
 * days, or a year of months. A switch at the top picks the mode: "One day" (a tap picks it and closes) or "Range"
 * (tap the start, then the end; it closes and applies on the end). Nothing after today (this month) can be picked,
 * and a range is at most 366 days (60 months).
 */
export function PeriodPicker({ granularity: g, from, to, onChange, label, ranges = true }: PeriodPickerProps) {
  const [open, setOpen] = useState(false);
  // The page shown: a month (days) or a year (months), as a Date at its start.
  const [view, setView] = useState(() => pageOf(g, to));
  const [mode, setMode] = useState<'single' | 'range'>(from === to ? 'single' : 'range');
  // The first end of a range being picked.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const side = usePopoverSide(open, rootRef, popoverRef);

  const close = () => {
    setOpen(false);
    setAnchor(null);
    setHover(null);
  };
  useDismiss(open, close, rootRef);

  const toggle = () => {
    if (open) return close();
    setView(pageOf(g, to));
    setMode(from === to ? 'single' : 'range');
    setOpen(true);
  };

  const changeMode = (m: 'single' | 'range') => {
    setMode(m);
    setAnchor(null);
    setHover(null);
  };

  const max = currentKey(g);
  const pick = (key: string) => {
    if (mode === 'single') {
      onChange(key, key);
      close();
      return;
    }
    // Range: the first tap marks the start (nothing reloads yet); the second applies it and closes.
    if (!anchor) {
      setAnchor(key);
      return;
    }
    onChange(key < anchor ? key : anchor, key < anchor ? anchor : key);
    close();
  };
  const choose = (p: Preset) => {
    onChange(p.from, p.to);
    close();
  };

  // While picking the second end: the range up to the hovered key; otherwise the selection.
  const [lo, hi] = anchor ? [hover && hover < anchor ? hover : anchor, hover && hover > anchor ? hover : anchor] : [from, to];
  const disabled = (key: string) =>
    key > max || (anchor !== null && (key > shiftKey(g, anchor, MAX_SPAN[g]) || key < shiftKey(g, anchor, -MAX_SPAN[g])));

  const keys = g === 'day' ? monthGrid(view).map((d) => d && toDateKey(d)) : yearMonths(view).map(toMonthKey);
  const canGoNext = g === 'day' ? toMonthKey(view) < max.slice(0, 7) : String(view.getFullYear()) < max.slice(0, 4);
  const step = (n: number) =>
    setView(g === 'day' ? new Date(view.getFullYear(), view.getMonth() + n, 1) : new Date(view.getFullYear() + n, 0, 1));

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${selectionLabel(g, from, to)}`}
        onClick={toggle}
      >
        <CalendarIcon size={16} />
        <span className={styles.value}>{selectionLabel(g, from, to)}</span>
        <ChevronDownIcon size={16} />
      </button>

      {open && (
        <div ref={popoverRef} role="dialog" aria-label={label} className={cx(styles.popover, side === 'left' && styles.alignLeft)}>
          {ranges && (
            <div className={styles.modes} role="group" aria-label="Pick">
              {(['single', 'range'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={cx(styles.mode, mode === m && styles.modeOn)}
                  aria-pressed={mode === m}
                  onClick={() => changeMode(m)}
                >
                  {m === 'range' ? 'Range' : g === 'day' ? 'One day' : 'One month'}
                </button>
              ))}
            </div>
          )}

          <div className={styles.head}>
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={<ChevronLeftIcon size={16} />}
              aria-label={g === 'day' ? 'Previous month' : 'Previous year'}
              onClick={() => step(-1)}
            />
            <span className={styles.title}>
              {g === 'day' ? view.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : view.getFullYear()}
            </span>
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={<ChevronRightIcon size={16} />}
              aria-label={g === 'day' ? 'Next month' : 'Next year'}
              onClick={() => step(1)}
              disabled={!canGoNext}
            />
          </div>

          <div className={cx(styles.grid, g === 'month' && styles.months)} role="grid" onPointerLeave={() => setHover(null)}>
            {g === 'day' &&
              WEEKDAYS.map((w) => (
                <span key={w} className={styles.weekday} aria-hidden>
                  {w}
                </span>
              ))}
            {keys.map((key, i) => {
              if (!key) return <span key={`blank-${i}`} />;
              const edge = key === lo || key === hi;
              const inside = key > lo && key < hi;
              const date = g === 'day' ? fromDateKey(key) : fromMonthKey(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={cx(styles.cell, edge && styles.selected, inside && styles.inRange, key === max && styles.current)}
                  aria-pressed={edge || inside}
                  aria-label={date.toLocaleDateString(
                    'en-GB',
                    g === 'day' ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' } : { month: 'long', year: 'numeric' },
                  )}
                  disabled={disabled(key)}
                  onClick={() => pick(key)}
                  onPointerEnter={() => anchor && setHover(key)}
                  onFocus={() => anchor && setHover(key)}
                >
                  {g === 'day' ? date.getDate() : date.toLocaleDateString('en-GB', { month: 'short' })}
                </button>
              );
            })}
          </div>

          <p className={styles.hint} aria-live="polite">
            {mode === 'single'
              ? `Tap a ${g === 'day' ? 'day' : 'month'}`
              : anchor
                ? `Now tap the last ${g === 'day' ? 'day' : 'month'}`
                : `Tap the first ${g === 'day' ? 'day' : 'month'} of the range`}
          </p>

          <div className={styles.foot}>
            {presets(g, ranges).map((p) => (
              <Button
                key={p.label}
                size="sm"
                variant={p.from === from && p.to === to ? 'primary' : 'outline'}
                onClick={() => choose(p)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** The page holding `key`: its month (days) or its year (months). */
function pageOf(g: ReportGranularity, key: string): Date {
  if (g === 'day') {
    const d = fromDateKey(key);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  return new Date(fromMonthKey(key).getFullYear(), 0, 1);
}

/** The month's days with leading blanks so the 1st lands under its weekday (weeks start on Sunday). */
function monthGrid(month: Date): (Date | null)[] {
  const blanks = Array<null>(month.getDay()).fill(null);
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: count }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1));
  return [...blanks, ...days];
}

function yearMonths(year: Date): Date[] {
  return Array.from({ length: 12 }, (_, i) => new Date(year.getFullYear(), i, 1));
}
